import { OperationPresenter } from "./OperationPresenter";
import { useCallback, useState } from "react";
import { useQuery } from "react-query";
import { getOperationLogs } from "api/actionLoggers";
import {
  GetOperationLogRequest,
  GetOperationLogResponse,
  GetOperationLogResponseResult,
  OperationType,
  Page,
} from "api/interfaces/operationlogInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { changeTimeByTimezone, getAccountTimeFormatDate } from "utils/timeUtil";
import { StatusType } from "components/blocks/status/Status";
import { TableColumnsType } from "antd";
import { valueToLabel } from "./menus";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { ColumnType } from "antd/es/table";
import styled from "styled-components";
import { calcRem } from "styles/theme";
import * as mediaQuery from "components/MediaQuery";
import moment from "moment";
import { toTrim } from "utils/functions";

type OperationLog = GetOperationLogResponseResult;

const PAGE_LIMIT = 20;

export interface SearchParams {
  username: string;
  accountNumber: string;
  page: Page | null;
  operationType: OperationType | null;
  timezone: string;
  startTime: string;
  endTime: string;
}

const initialSearchParams = (timezone: string): SearchParams => {
  const now = moment().tz(timezone).utc().toISOString();
  const oneHourBefore = moment()
    .tz(timezone)
    .add(-2, "hour")
    .utc()
    .toISOString();
  return {
    username: "",
    accountNumber: "",
    page: null,
    operationType: null,
    timezone: timezone,
    startTime: oneHourBefore,
    endTime: now,
  };
};

const initialPagination: BasePagination = {
  total: 0,
  pageNum: 0,
  pageLimit: PAGE_LIMIT,
  sortType: "createdAt",
  sortDirection: SORT_DIRECTION.DESC,
};

const stateToRequestParams = (
  state: SearchParams & BasePagination
): GetOperationLogRequest => {
  const requestParams: GetOperationLogRequest = {
    username: state.username,
    accountNumber: state.accountNumber,
    timezone: "UTC",
    startTime: state.startTime,
    endTime: state.endTime,
    total: 0,
    pageNum: state.pageNum,
    pageLimit: PAGE_LIMIT,
    sortType: state.sortType,
    sortDirection: state.sortDirection,
  };
  if (state.page) {
    requestParams.page = state.page;
  }
  if (state.operationType) {
    requestParams.operationType = state.operationType;
  }
  return requestParams;
};

export default function OperationContainer(): JSX.Element {
  const [operationLogList, setOperationLogList] = useState<OperationLog[]>([]);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [searchParams, setSearchParams] = useState<SearchParams>(
    initialSearchParams(selectedAccount.timezone)
  );
  const [paginationInfo, setPaginationInfo] = useState<BasePagination>({
    ...initialPagination,
  });
  const isBigScreen = mediaQuery.useIsBigScreen();
  const isDesktop = mediaQuery.useIsDesktop();
  const [status, setStatus] = useState<StatusType>({
    totalTitle: "Total",
    totalValue: 0,
    items: [],
    isMobileShow: {
      totalTitle: true,
      totalValue: true,
      items: true,
    },
    cloudStatus: false,
  });

  const onHeaderCell = (column: ColumnType<OperationLog>) => {
    return {
      onClick: (e: any) => {
        if (!column.key) {
          return;
        }
        setPaginationInfo((info) => {
          return {
            ...info,
            sortType: column.key as string,
            sortDirection: onSortAntd(info.sortDirection),
            pageNum: 0,
          };
        });
      },
    };
  };
  const columns: TableColumnsType<OperationLog> = [
    {
      title: "Menu",
      width: isDesktop ? 150 : 100,
      render: (row) => valueToLabel(row.page) || "-",
    },
    {
      title: "Date Time",
      key: "createdAt",
      render: (value, row) => row.time,
      sorter: true,
      sortOrder: changeSortOrderAntd(paginationInfo, "createdAt"),
      defaultSortOrder: "descend",
      onHeaderCell,
      width: 200,
    },
    {
      title: "Organization",
      responsive: ["xl"],
      render: (row) => row.accountNumber || "-",
      width: 100,
    },
    {
      title: "User",
      render: (row) => row.username || "-",
      width: isBigScreen || !isDesktop ? undefined : "20%",
    },
    {
      title: "Action",
      width: 100,
      render: (row) => row.operationType || "-",
    },
    {
      title: "Result",
      width: 100,
      align: "center",
      render: (row) => {
        return (
          <StatusWrapper status={row.status}>
            {!row.status
              ? "-"
              : row.status >= 200 && row.status < 300
              ? "Success"
              : "Fail"}
          </StatusWrapper>
        );
      },
    },
    {
      title: "Reason",
      width: isBigScreen ? "45%" : undefined,
      responsive: ["lg"],
      ellipsis: false,
      render: (row) => row.errorStatus,
    },
  ];
  const { refetch } = useQuery(
    ["operationLog", paginationInfo],
    () =>
      getOperationLogs(
        stateToRequestParams({
          ...{
            ...searchParams,
          },
          ...paginationInfo,
        }),
        selectedAccount.accountId
      ),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: GetOperationLogResponse) => {
        if (res.error || res.errorString) {
          setPaginationInfo({ ...initialPagination });
          setOperationLogList([]);
          return;
        }
        setPaginationInfo({ ...res.page });
        setStatus((prev) => {
          return {
            ...prev,
            totalValue: res.page.total,
          };
        });
        const logs = res.result.map((log) => {
          return {
            ...log,
            time: getAccountTimeFormatDate(
              log.time,
              {
                ...selectedAccount,
                timezone: searchParams.timezone,
              },
              true,
              true
            ),
          };
        });
        setOperationLogList(logs);
      },
      onError: (err: any) => {
        console.log(err);
      },
    }
  );

  const onClickSearch = () => {
    setPaginationInfo((paginationInfo) => ({
      ...paginationInfo,
      accountNumber: toTrim(searchParams.accountNumber),
      username: toTrim(searchParams.username),
      operationType: toTrim(searchParams.operationType as string),
      startTime: toTrim(searchParams.startTime),
      endTime: toTrim(searchParams.endTime),
      timezone: toTrim(searchParams.timezone),
      pageNum: 0,
    }));
    refetch();
  };

  const onChangePage = (page: number) => {
    setPaginationInfo((paginationInfo) => ({
      ...paginationInfo,
      pageNum: page - 1,
    }));
  };

  const onSearchParamChange = useCallback(
    (name: string, value: any) => {
      setSearchParams((searchParams) => {
        if (name === "timezone") {
          return {
            ...searchParams,
            [name]: value,
            startTime: changeTimeByTimezone(
              searchParams.startTime,
              value,
              selectedAccount
            ),
            endTime: changeTimeByTimezone(
              searchParams.endTime,
              value,
              selectedAccount
            ),
          };
        }
        return {
          ...searchParams,
          [name]: value,
        };
      });
    },
    [selectedAccount]
  );

  return (
    <OperationPresenter
      data={operationLogList}
      accountInfo={selectedAccount}
      onSearchParamChange={onSearchParamChange}
      onClickSearch={onClickSearch}
      searchParams={searchParams}
      paginationInfo={paginationInfo}
      setPaginationInfo={setPaginationInfo}
      onChangePage={onChangePage}
      status={status}
      columns={columns}
    />
  );
}

const StatusWrapper = styled.div<{ status: number }>`
  font-size: ${calcRem(14)};
  color: ${({ theme, status }) =>
    !status
      ? theme.COLORS.WHITE
      : status >= 200 && status < 300
      ? theme.COLORS.GREEN
      : theme.COLORS.RED};
`;
